import {
  MousePointer,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Type,
  Palette,
  Sliders,
  RotateCcw,
} from "lucide-react";
import { useEditorStore } from "../store";
import { findNodeById } from "../tree-utils";
import type { Href } from "@zirro/schema";

import { SectionCustomizer } from "./SectionCustomizer";
import { ImageFrameControls } from "./ImageFrameControls";
import { HrefPicker, LinkListEditor, type NavLinkValue } from "./LinkListEditor";
import { EMAIL_PATTERN, PHONE_PATTERN, ValidatedInput, optional, required } from "./ValidatedInput";

const FONT_OPTIONS = [
  { id: "inter", label: "Inter (Modern Sans)" },
  { id: "roboto", label: "Roboto (Neutral Sans)" },
  { id: "open-sans", label: "Open Sans (Friendly Sans)" },
  { id: "lato", label: "Lato (Professional Sans)" },
  { id: "montserrat", label: "Montserrat (Geometric Display)" },
  { id: "dm-sans", label: "DM Sans (Minimalist)" },
  { id: "space-grotesk", label: "Space Grotesk (Tech Sans)" },
  { id: "ibm-plex-sans", label: "IBM Plex Sans (Corporate)" },
  { id: "playfair-display", label: "Playfair Display (Editorial Serif)" },
  { id: "merriweather", label: "Merriweather (Literary Serif)" },
  { id: "source-serif-4", label: "Source Serif 4 (Refined Serif)" },
  { id: "libre-baskerville", label: "Libre Baskerville (Classic Serif)" },
];

const QUICK_COLORS = [
  { label: "White", value: "#ffffff" },
  { label: "Dark", value: "#0f172a" },
  { label: "Primary", value: "var(--zirro-color-primary)" },
  { label: "Secondary", value: "var(--zirro-color-secondary)" },
  { label: "Accent", value: "var(--zirro-color-accent)" },
  { label: "Muted", value: "var(--zirro-color-muted)" },
];

export function InspectorContent() {
  const document = useEditorStore((s) => s.document);
  const selectedNodeId = useEditorStore((s) => s.selectedNodeId);
  const updateNodeProps = useEditorStore((s) => s.updateNodeProps);
  const openMediaModal = useEditorStore((s) => s.openMediaModal);

  if (!selectedNodeId) {
    return (
      <div className="zirro-inspector-empty">
        <MousePointer size={24} style={{ color: "var(--ed-text-dim)" }} />
        <p>No component selected.</p>
        <p className="zirro-hint">
          Click an element on the canvas to edit its properties and styling.
        </p>
      </div>
    );
  }

  const search = findNodeById(document, selectedNodeId);
  if (!search) {
    return (
      <div className="zirro-inspector-empty">
        <p>Component not found.</p>
      </div>
    );
  }

  const { node } = search;
  if (node.type === "Section") {
    return <SectionCustomizer node={node} />;
  }
  const props = node.props ?? {};

  const handlePropChange = (key: string, value: unknown) => {
    updateNodeProps(node.id, { [key]: value });
  };

  const pages = document.pages;

  return (
    <div className="zirro-inspector-section">
      <div className="zirro-inspector-header">
        <h4>{node.type} Component</h4>
      </div>

      {/* ── HEADING ELEMENT ─────────────────────────────────────────────────── */}
      {node.type === "Heading" ? (
        <>
          <label className="zirro-field">
            <span>Heading Text</span>
            <textarea
              value={String(props.text ?? "")}
              onChange={(e) => handlePropChange("text", e.target.value)}
              rows={3}
            />
          </label>

          <label className="zirro-field">
            <span>Heading Level</span>
            <select
              value={Number(props.level ?? 2)}
              onChange={(e) => handlePropChange("level", Number(e.target.value))}
            >
              <option value={1}>H1 — Main Page Title</option>
              <option value={2}>H2 — Section Title</option>
              <option value={3}>H3 — Subsection Title</option>
              <option value={4}>H4 — Feature Heading</option>
              <option value={5}>H5 — Minor Heading</option>
              <option value={6}>H6 — Small Label</option>
            </select>
          </label>

          {/* Typography Controls */}
          <div className="zirro-field-group">
            <h5>
              <Type size={12} style={{ display: "inline", marginRight: 4 }} />
              Typography & Font
            </h5>

            <label className="zirro-field">
              <span>Font Family</span>
              <select
                value={String(props.fontFamily ?? "inherit")}
                onChange={(e) =>
                  handlePropChange("fontFamily", e.target.value === "inherit" ? undefined : e.target.value)
                }
              >
                <option value="inherit">Inherit Theme / Section Font</option>
                {FONT_OPTIONS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Font Size</span>
                <select
                  value={String(props.fontSize ?? "3xl")}
                  onChange={(e) => handlePropChange("fontSize", e.target.value)}
                >
                  <option value="sm">SM (14px)</option>
                  <option value="base">Base (16px)</option>
                  <option value="lg">LG (18px)</option>
                  <option value="xl">XL (20px)</option>
                  <option value="2xl">2XL (24px)</option>
                  <option value="3xl">3XL (30px)</option>
                  <option value="4xl">4XL (36px)</option>
                  <option value="5xl">5XL (48px)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Font Weight</span>
                <select
                  value={Number(props.weight ?? 700)}
                  onChange={(e) => handlePropChange("weight", Number(e.target.value))}
                >
                  <option value={400}>400 — Regular</option>
                  <option value={500}>500 — Medium</option>
                  <option value={600}>600 — Semibold</option>
                  <option value={700}>700 — Bold</option>
                  <option value={800}>800 — Extra Bold</option>
                </select>
              </label>
            </div>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Line Height</span>
                <select
                  value={String(props.lineHeight ?? "1.25")}
                  onChange={(e) => handlePropChange("lineHeight", e.target.value)}
                >
                  <option value="1.1">1.1 (Compact)</option>
                  <option value="1.25">1.25 (Standard Heading)</option>
                  <option value="1.4">1.4 (Spacious)</option>
                  <option value="1.6">1.6 (Relaxed)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Letter Spacing</span>
                <select
                  value={String(props.letterSpacing ?? "normal")}
                  onChange={(e) =>
                    handlePropChange("letterSpacing", e.target.value === "normal" ? undefined : e.target.value)
                  }
                >
                  <option value="normal">Normal</option>
                  <option value="-0.02em">Tight (-0.02em)</option>
                  <option value="0.05em">Wide (+0.05em)</option>
                  <option value="0.1em">Wider (+0.1em)</option>
                </select>
              </label>
            </div>

            {/* Alignment */}
            <div className="zirro-field">
              <span>Text Alignment</span>
              <div className="zirro-segmented-control">
                {[
                  { value: "left", label: "Left", icon: AlignLeft },
                  { value: "center", label: "Center", icon: AlignCenter },
                  { value: "right", label: "Right", icon: AlignRight },
                  { value: "justify", label: "Justify", icon: AlignJustify },
                ].map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`zirro-segment ${props.align === opt.value ? "active" : ""}`}
                      onClick={() => handlePropChange("align", opt.value)}
                      title={opt.label}
                    >
                      <Icon size={13} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Transform & Decoration */}
            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Text Transform</span>
                <select
                  value={String(props.textTransform ?? "none")}
                  onChange={(e) =>
                    handlePropChange("textTransform", e.target.value === "none" ? undefined : e.target.value)
                  }
                >
                  <option value="none">Normal</option>
                  <option value="uppercase">UPPERCASE</option>
                  <option value="lowercase">lowercase</option>
                  <option value="capitalize">Capitalize</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Decoration</span>
                <select
                  value={String(props.textDecoration ?? "none")}
                  onChange={(e) =>
                    handlePropChange("textDecoration", e.target.value === "none" ? undefined : e.target.value)
                  }
                >
                  <option value="none">None</option>
                  <option value="underline">Underline</option>
                  <option value="line-through">Strikethrough</option>
                </select>
              </label>
            </div>
          </div>

          {/* Color & Contrast Controls */}
          <div className="zirro-field-group">
            <h5>
              <Palette size={12} style={{ display: "inline", marginRight: 4 }} />
              Heading Color & Contrast
            </h5>

            <div className="zirro-field">
              <span>Text Color</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(props.textColor || props.color || "#0f172a").startsWith("#")
                      ? String(props.textColor || props.color || "#0f172a")
                      : "#0f172a"
                  }
                  onChange={(e) => {
                    handlePropChange("textColor", e.target.value);
                    handlePropChange("color", e.target.value);
                  }}
                />
                <input
                  type="text"
                  placeholder="e.g. #ffffff or #0f172a"
                  value={String(props.textColor || props.color || "")}
                  onChange={(e) => {
                    handlePropChange("textColor", e.target.value);
                    handlePropChange("color", e.target.value);
                  }}
                  className="zirro-hex-text-input"
                />
              </div>

              {/* Quick Swatches */}
              <div className="zirro-quick-tint-buttons" style={{ marginTop: 6 }}>
                {QUICK_COLORS.map((qc) => (
                  <button
                    key={qc.label}
                    type="button"
                    className="zirro-ed-btn"
                    onClick={() => {
                      handlePropChange("textColor", qc.value);
                      handlePropChange("color", qc.value);
                    }}
                  >
                    {qc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional Background Highlight */}
            <div className="zirro-field">
              <span>Text Background / Highlight (Optional)</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(props.backgroundColor ?? "#ffffff").startsWith("#")
                      ? String(props.backgroundColor)
                      : "#ffffff"
                  }
                  onChange={(e) => handlePropChange("backgroundColor", e.target.value)}
                />
                <input
                  type="text"
                  placeholder="None / transparent"
                  value={String(props.backgroundColor ?? "")}
                  onChange={(e) => handlePropChange("backgroundColor", e.target.value || undefined)}
                  className="zirro-hex-text-input"
                />
                {props.backgroundColor ? (
                  <button
                    type="button"
                    className="zirro-btn-mini"
                    onClick={() => handlePropChange("backgroundColor", undefined)}
                    title="Clear Background"
                  >
                    <RotateCcw size={12} />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Spacing & Max Width */}
          <div className="zirro-field-group">
            <h5>
              <Sliders size={12} style={{ display: "inline", marginRight: 4 }} />
              Spacing & Max Width
            </h5>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Margin Top</span>
                <select
                  value={String(props.marginTop ?? "0px")}
                  onChange={(e) => handlePropChange("marginTop", e.target.value)}
                >
                  <option value="0px">0px (None)</option>
                  <option value="8px">8px (SM)</option>
                  <option value="16px">16px (MD)</option>
                  <option value="24px">24px (LG)</option>
                  <option value="32px">32px (XL)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Margin Bottom</span>
                <select
                  value={String(props.marginBottom ?? "12px")}
                  onChange={(e) => handlePropChange("marginBottom", e.target.value)}
                >
                  <option value="0px">0px</option>
                  <option value="8px">8px (Compact)</option>
                  <option value="12px">12px (Standard)</option>
                  <option value="20px">20px (Spacious)</option>
                  <option value="32px">32px (Spaced)</option>
                </select>
              </label>
            </div>

            <label className="zirro-field">
              <span>Block Max Width</span>
              <select
                value={String(props.maxWidth ?? "100%")}
                onChange={(e) =>
                  handlePropChange("maxWidth", e.target.value === "100%" ? undefined : e.target.value)
                }
              >
                <option value="100%">Full Width (100%)</option>
                <option value="480px">Narrow (480px — Punchy headline)</option>
                <option value="640px">Medium (640px — Balanced)</option>
                <option value="800px">Wide (800px)</option>
              </select>
            </label>
          </div>
        </>
      ) : null}

      {/* ── TEXT / PARAGRAPH ELEMENT ────────────────────────────────────────── */}
      {node.type === "Text" ? (
        <>
          <label className="zirro-field">
            <span>Paragraph Text</span>
            <textarea
              value={
                Array.isArray(props.content) && props.content[0]
                  ? String((props.content[0] as { text: string }).text ?? "")
                  : ""
              }
              onChange={(e) =>
                handlePropChange("content", [{ text: e.target.value }])
              }
              rows={5}
            />
          </label>

          {/* Typography Controls */}
          <div className="zirro-field-group">
            <h5>
              <Type size={12} style={{ display: "inline", marginRight: 4 }} />
              Paragraph Typography
            </h5>

            <label className="zirro-field">
              <span>Font Family</span>
              <select
                value={String(props.fontFamily ?? "inherit")}
                onChange={(e) =>
                  handlePropChange("fontFamily", e.target.value === "inherit" ? undefined : e.target.value)
                }
              >
                <option value="inherit">Inherit Theme / Section Body Font</option>
                {FONT_OPTIONS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Font Size</span>
                <select
                  value={String(props.size ?? "base")}
                  onChange={(e) => handlePropChange("size", e.target.value)}
                >
                  <option value="xs">XS (12px — Small caption)</option>
                  <option value="sm">SM (14px — Compact)</option>
                  <option value="base">Base (16px — Standard body)</option>
                  <option value="lg">LG (18px — Lead text)</option>
                  <option value="xl">XL (20px — Subheading)</option>
                  <option value="2xl">2XL (24px — Large lead)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Font Weight</span>
                <select
                  value={Number(props.weight ?? 400)}
                  onChange={(e) => handlePropChange("weight", Number(e.target.value))}
                >
                  <option value={300}>300 — Light</option>
                  <option value={400}>400 — Regular</option>
                  <option value={500}>500 — Medium</option>
                  <option value={600}>600 — Semibold</option>
                  <option value={700}>700 — Bold</option>
                </select>
              </label>
            </div>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Line Height</span>
                <select
                  value={String(props.lineHeight ?? "1.6")}
                  onChange={(e) => handlePropChange("lineHeight", e.target.value)}
                >
                  <option value="1.3">1.3 (Snug)</option>
                  <option value="1.5">1.5 (Standard)</option>
                  <option value="1.6">1.6 (Readable body)</option>
                  <option value="1.8">1.8 (Relaxed)</option>
                  <option value="2.0">2.0 (Spacious)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Letter Spacing</span>
                <select
                  value={String(props.letterSpacing ?? "normal")}
                  onChange={(e) =>
                    handlePropChange("letterSpacing", e.target.value === "normal" ? undefined : e.target.value)
                  }
                >
                  <option value="normal">Normal</option>
                  <option value="-0.01em">Tight (-0.01em)</option>
                  <option value="0.02em">Wide (+0.02em)</option>
                  <option value="0.05em">Wider (+0.05em)</option>
                </select>
              </label>
            </div>

            {/* Alignment */}
            <div className="zirro-field">
              <span>Alignment</span>
              <div className="zirro-segmented-control">
                {[
                  { value: "left", label: "Left", icon: AlignLeft },
                  { value: "center", label: "Center", icon: AlignCenter },
                  { value: "right", label: "Right", icon: AlignRight },
                  { value: "justify", label: "Justify", icon: AlignJustify },
                ].map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`zirro-segment ${props.align === opt.value ? "active" : ""}`}
                      onClick={() => handlePropChange("align", opt.value)}
                      title={opt.label}
                    >
                      <Icon size={13} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Color & Contrast Controls */}
          <div className="zirro-field-group">
            <h5>
              <Palette size={12} style={{ display: "inline", marginRight: 4 }} />
              Paragraph Color & Contrast
            </h5>

            <div className="zirro-field">
              <span>Text Color</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(props.textColor || props.color || "#0f172a").startsWith("#")
                      ? String(props.textColor || props.color || "#0f172a")
                      : "#0f172a"
                  }
                  onChange={(e) => {
                    handlePropChange("textColor", e.target.value);
                    handlePropChange("color", e.target.value);
                  }}
                />
                <input
                  type="text"
                  placeholder="e.g. #ffffff or #0f172a"
                  value={String(props.textColor || props.color || "")}
                  onChange={(e) => {
                    handlePropChange("textColor", e.target.value);
                    handlePropChange("color", e.target.value);
                  }}
                  className="zirro-hex-text-input"
                />
              </div>

              {/* Quick Swatches */}
              <div className="zirro-quick-tint-buttons" style={{ marginTop: 6 }}>
                {QUICK_COLORS.map((qc) => (
                  <button
                    key={qc.label}
                    type="button"
                    className="zirro-ed-btn"
                    onClick={() => {
                      handlePropChange("textColor", qc.value);
                      handlePropChange("color", qc.value);
                    }}
                  >
                    {qc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Optional Background Highlight */}
            <div className="zirro-field">
              <span>Background Highlight (Optional)</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(props.backgroundColor ?? "#ffffff").startsWith("#")
                      ? String(props.backgroundColor)
                      : "#ffffff"
                  }
                  onChange={(e) => handlePropChange("backgroundColor", e.target.value)}
                />
                <input
                  type="text"
                  placeholder="None / transparent"
                  value={String(props.backgroundColor ?? "")}
                  onChange={(e) => handlePropChange("backgroundColor", e.target.value || undefined)}
                  className="zirro-hex-text-input"
                />
                {props.backgroundColor ? (
                  <button
                    type="button"
                    className="zirro-btn-mini"
                    onClick={() => handlePropChange("backgroundColor", undefined)}
                    title="Clear Background"
                  >
                    <RotateCcw size={12} />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Spacing & Max Width */}
          <div className="zirro-field-group">
            <h5>
              <Sliders size={12} style={{ display: "inline", marginRight: 4 }} />
              Paragraph Spacing & Width
            </h5>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Margin Top</span>
                <select
                  value={String(props.marginTop ?? "0px")}
                  onChange={(e) => handlePropChange("marginTop", e.target.value)}
                >
                  <option value="0px">0px</option>
                  <option value="8px">8px (SM)</option>
                  <option value="16px">16px (MD)</option>
                  <option value="24px">24px (LG)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Margin Bottom</span>
                <select
                  value={String(props.marginBottom ?? "16px")}
                  onChange={(e) => handlePropChange("marginBottom", e.target.value)}
                >
                  <option value="0px">0px</option>
                  <option value="8px">8px (Compact)</option>
                  <option value="16px">16px (Standard)</option>
                  <option value="24px">24px (Spacious)</option>
                </select>
              </label>
            </div>

            <label className="zirro-field">
              <span>Line Max Width (Readability)</span>
              <select
                value={String(props.maxWidth ?? "100%")}
                onChange={(e) =>
                  handlePropChange("maxWidth", e.target.value === "100%" ? undefined : e.target.value)
                }
              >
                <option value="100%">Full Width (100%)</option>
                <option value="480px">Compact (480px — Quick scan)</option>
                <option value="640px">Optimal Reading Width (640px — 65–75 chars/line)</option>
                <option value="768px">Standard (768px)</option>
              </select>
            </label>
          </div>
        </>
      ) : null}

      {/* ── BUTTON ELEMENT ─────────────────────────────────────────────────── */}
      {node.type === "Button" ? (
        <>
          <label className="zirro-field">
            <span>Button Label</span>
            <input
              type="text"
              value={String(props.label ?? "Button")}
              onChange={(e) => handlePropChange("label", e.target.value)}
            />
          </label>

          <div className="zirro-field">
            <span>Link Destination</span>
            {(() => {
              const href = (props.href ?? { type: "url", url: "#" }) as Href;
              const isPage = href.type === "page";

              return (
                <div className="zirro-href-picker">
                  <div className="zirro-radio-group">
                    <label>
                      <input
                        type="radio"
                        name="hrefType"
                        checked={isPage}
                        onChange={() =>
                          handlePropChange("href", {
                            type: "page",
                            pageId: pages[0]?.id,
                          })
                        }
                      />
                      Internal Page
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="hrefType"
                        checked={!isPage}
                        onChange={() =>
                          handlePropChange("href", {
                            type: "url",
                            url: "https://",
                          })
                        }
                      />
                      External URL / Mail
                    </label>
                  </div>

                  {isPage ? (
                    <select
                      value={href.type === "page" ? href.pageId : ""}
                      onChange={(e) =>
                        handlePropChange("href", {
                          type: "page",
                          pageId: e.target.value,
                        })
                      }
                    >
                      {pages.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title} ({p.slug})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={href.type === "url" ? href.url : ""}
                      placeholder="https://example.com"
                      onChange={(e) =>
                        handlePropChange("href", {
                          type: "url",
                          url: e.target.value,
                        })
                      }
                    />
                  )}
                </div>
              );
            })()}
          </div>

          <div className="zirro-field-group">
            <h5>Button Appearance & Colors</h5>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span>Style Variant</span>
                <select
                  value={String(props.variant ?? "primary")}
                  onChange={(e) => handlePropChange("variant", e.target.value)}
                >
                  <option value="primary">Primary (Filled)</option>
                  <option value="secondary">Secondary (Accent)</option>
                  <option value="outline">Outline (Bordered)</option>
                  <option value="ghost">Ghost (Subtle)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span>Button Size</span>
                <select
                  value={String(props.size ?? "md")}
                  onChange={(e) => handlePropChange("size", e.target.value)}
                >
                  <option value="sm">Small (Compact)</option>
                  <option value="md">Medium (Standard)</option>
                  <option value="lg">Large (Prominent Call-to-Action)</option>
                </select>
              </label>
            </div>

            {/* Custom Button Colors */}
            <div className="zirro-field">
              <span>Custom Background Color</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(props.backgroundColor ?? "#4f46e5").startsWith("#")
                      ? String(props.backgroundColor)
                      : "#4f46e5"
                  }
                  onChange={(e) => handlePropChange("backgroundColor", e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Inherit variant background"
                  value={String(props.backgroundColor ?? "")}
                  onChange={(e) => handlePropChange("backgroundColor", e.target.value || undefined)}
                  className="zirro-hex-text-input"
                />
                {props.backgroundColor ? (
                  <button
                    type="button"
                    className="zirro-btn-mini"
                    onClick={() => handlePropChange("backgroundColor", undefined)}
                    title="Reset to variant"
                  >
                    <RotateCcw size={12} />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="zirro-field">
              <span>Custom Text Color</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(props.textColor ?? "#ffffff").startsWith("#")
                      ? String(props.textColor)
                      : "#ffffff"
                  }
                  onChange={(e) => handlePropChange("textColor", e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Inherit variant text color"
                  value={String(props.textColor ?? "")}
                  onChange={(e) => handlePropChange("textColor", e.target.value || undefined)}
                  className="zirro-hex-text-input"
                />
                {props.textColor ? (
                  <button
                    type="button"
                    className="zirro-btn-mini"
                    onClick={() => handlePropChange("textColor", undefined)}
                    title="Reset to variant"
                  >
                    <RotateCcw size={12} />
                  </button>
                ) : null}
              </div>
            </div>

            {/* Border Radius */}
            <label className="zirro-field">
              <span>Corner Radius</span>
              <select
                value={String(props.borderRadius ?? "inherit")}
                onChange={(e) =>
                  handlePropChange("borderRadius", e.target.value === "inherit" ? undefined : e.target.value)
                }
              >
                <option value="inherit">Theme Default</option>
                <option value="none">Sharp Corners (0px)</option>
                <option value="sm">Subtle (4px)</option>
                <option value="md">Rounded (8px)</option>
                <option value="lg">Smooth (16px)</option>
                <option value="full">Pill / Fully Rounded</option>
              </select>
            </label>
          </div>
        </>
      ) : null}

      {/* ── IMAGE ELEMENT ──────────────────────────────────────────────────── */}
      {node.type === "Image" ? (
        <>
          <ImageFrameControls node={node} />

          <label className="zirro-field">
            <span>Or paste an image link</span>
            <input
              type="text"
              value={String(props.src ?? "")}
              placeholder="https://…"
              onChange={(e) => handlePropChange("src", e.target.value)}
            />
          </label>

          <label className="zirro-field">
            <span>Alt Description</span>
            <input
              type="text"
              value={String(props.alt ?? "")}
              placeholder="Descriptive text for accessibility"
              onChange={(e) => handlePropChange("alt", e.target.value)}
            />
          </label>

          <label className="zirro-field">
            <span>Alignment</span>
            <select
              value={String(props.align ?? "center")}
              onChange={(e) => handlePropChange("align", e.target.value)}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </label>

          <label className="zirro-field">
            <span>Max Width</span>
            <select
              value={String(props.width ?? "contained")}
              onChange={(e) => handlePropChange("width", e.target.value)}
            >
              <option value="contained">Normal (480px)</option>
              <option value="half">Half (50%)</option>
              <option value="full">Full Width (100%)</option>
            </select>
          </label>

          <label className="zirro-field zirro-checkbox-label">
            <input
              type="checkbox"
              checked={Boolean(props.rounded)}
              onChange={(e) => handlePropChange("rounded", e.target.checked)}
            />
            <span>Rounded Corners</span>
          </label>
        </>
      ) : null}

      {/* ── CARD ELEMENT ───────────────────────────────────────────────────── */}
      {node.type === "Card" ? (
        <>
          <label className="zirro-field">
            <span>Card Title</span>
            <input
              type="text"
              value={String(props.title ?? "")}
              onChange={(e) => handlePropChange("title", e.target.value)}
            />
          </label>
          <label className="zirro-field">
            <span>Card Description</span>
            <textarea
              value={String(props.description ?? "")}
              onChange={(e) => handlePropChange("description", e.target.value)}
              rows={3}
            />
          </label>
        </>
      ) : null}

      {/* ── FEATURE GRID ELEMENT ───────────────────────────────────────────── */}
      {node.type === "FeatureGrid" ? (
        <label className="zirro-field">
          <span>Columns</span>
          <select
            value={Number(props.columns ?? 3)}
            onChange={(e) => handlePropChange("columns", Number(e.target.value))}
          >
            <option value={1}>1 Column</option>
            <option value={2}>2 Columns</option>
            <option value={3}>3 Columns</option>
            <option value={4}>4 Columns</option>
          </select>
        </label>
      ) : null}

      {/* ── BADGE ELEMENT ──────────────────────────────────────────────────── */}
      {node.type === "Badge" ? (
        <label className="zirro-field">
          <span>Badge Text</span>
          <input
            type="text"
            value={String(props.text ?? "")}
            onChange={(e) => handlePropChange("text", e.target.value)}
          />
        </label>
      ) : null}

      {/* ── HEADER ELEMENT ─────────────────────────────────────────────────── */}
      {node.type === "Header" ? (
        <>
          <label className="zirro-field">
            <span>Logo Display Type</span>
            <select
              value={String(props.logoType ?? "both")}
              onChange={(e) => handlePropChange("logoType", e.target.value)}
            >
              <option value="both">Logo Image & Site Title</option>
              <option value="image">Logo Image Only</option>
              <option value="text">Site Title Only</option>
            </select>
          </label>

          <label className="zirro-field">
            <span>Site Title Text</span>
            <input
              type="text"
              value={String(props.logoText ?? "")}
              onChange={(e) => handlePropChange("logoText", e.target.value)}
            />
          </label>

          <label className="zirro-field">
            <span>Logo Image URL</span>
            <div className="zirro-input-with-btn">
              <input
                type="text"
                value={String(props.logoSrc ?? "")}
                placeholder="https://…"
                onChange={(e) => handlePropChange("logoSrc", e.target.value)}
              />
              <button
                type="button"
                className="zirro-btn zirro-btn-secondary"
                onClick={() =>
                  openMediaModal((url) => handlePropChange("logoSrc", url))
                }
              >
                Browse
              </button>
            </div>
          </label>

          <label className="zirro-field">
            <span>Logo Height (px)</span>
            <input
              type="number"
              min={16}
              max={100}
              value={Number(String(props.logoHeight ?? "36").replace("px", ""))}
              onChange={(e) => handlePropChange("logoHeight", Number(e.target.value))}
            />
          </label>

          <LinkListEditor
            title="Menu links"
            links={Array.isArray(props.links) ? (props.links as NavLinkValue[]) : []}
            pages={pages}
            onChange={(links) => handlePropChange("links", links)}
          />

          <div className="zirro-field-group">
            <h5>Header button</h5>
            <label className="zirro-field zirro-checkbox-label">
              <input
                type="checkbox"
                checked={Boolean(props.cta)}
                onChange={(e) =>
                  handlePropChange(
                    "cta",
                    e.target.checked
                      ? { label: "Contact us", href: { type: "page", pageId: (pages.find((p) => p.slug === "/contact") ?? pages[0])?.id } }
                      : undefined,
                  )
                }
              />
              <span>Show a button in the header</span>
            </label>
            {props.cta ? (
              <>
                <label className="zirro-field">
                  <span>Button text</span>
                  <ValidatedInput
                    type="text"
                    maxLength={40}
                    value={(props.cta as NavLinkValue).label}
                    validate={required("Button text can't be empty")}
                    onCommit={(label) => handlePropChange("cta", { ...(props.cta as NavLinkValue), label })}
                  />
                </label>
                <HrefPicker
                  value={(props.cta as NavLinkValue).href}
                  pages={pages}
                  onChange={(href) => handlePropChange("cta", { ...(props.cta as NavLinkValue), href })}
                />
              </>
            ) : null}
          </div>
        </>
      ) : null}

      {/* ── FOOTER ELEMENT ─────────────────────────────────────────────────── */}
      {node.type === "Footer" ? (
        <>
          <label className="zirro-field">
            <span>Business Name</span>
            <input
              type="text"
              value={String(props.businessName ?? "")}
              onChange={(e) => handlePropChange("businessName", e.target.value)}
            />
          </label>
          <label className="zirro-field">
            <span>Copyright Notice</span>
            <input
              type="text"
              value={String(props.copyright ?? "")}
              onChange={(e) => handlePropChange("copyright", e.target.value)}
            />
          </label>
          <label className="zirro-field">
            <span>Email</span>
            <ValidatedInput
              type="email"
              value={String(props.email ?? "")}
              placeholder="hello@yourbusiness.com"
              validate={optional(EMAIL_PATTERN, "Enter an email like hello@yourbusiness.com")}
              onCommit={(value) => handlePropChange("email", value || undefined)}
            />
          </label>
          <label className="zirro-field">
            <span>Phone</span>
            <ValidatedInput
              type="text"
              inputMode="tel"
              value={String(props.phone ?? "")}
              placeholder="+234 800 000 0000"
              validate={optional(PHONE_PATTERN, "Use digits, spaces, + ( ) or -, at least 7")}
              onCommit={(value) => handlePropChange("phone", value || undefined)}
            />
          </label>

          <LinkListEditor
            title="Footer links"
            links={Array.isArray(props.links) ? (props.links as NavLinkValue[]) : []}
            pages={pages}
            onChange={(links) => handlePropChange("links", links)}
          />
        </>
      ) : null}
    </div>
  );
}
